import type {
  ShipmentReportSplit,
  ShipmentsToDateData,
} from '../../utils/client-reports/types';
import CommercialPlanChart from './CommercialPlanChart';
import {
  EmptySection,
  Metric,
  MetricStrip,
  Panel,
  ProgressLine,
  ReportTable,
  SectionHeader,
  SplitBar,
  SplitLine,
  StatusLegend,
} from './ReportBlocks';
import {
  formatPlanAmount,
  PLAN_UNIT_LABELS,
  titleCase,
  TRANSPORT_LABELS,
  useReportLayout,
} from './reportLayout';
import { useReportFormat } from './useReportFormat';

const total = (split: ShipmentReportSplit, key: 'shipments' | 'ceq') =>
  split.arrived[key] + split.inTransit[key];

type PortRow = ShipmentsToDateData['ports'][number];

const PortsPanel = ({ data }: { data: ShipmentsToDateData }) => {
  const { t, number } = useReportFormat();
  const max = Math.max(
    ...data.ports.map((port) => total(port, 'shipments')),
    0
  );

  return (
    <Panel
      title={t('Embarques por puerto de destino')}
      aside={<StatusLegend />}
    >
      <ReportTable<PortRow>
        label={t('Embarques por puerto de destino')}
        rows={data.ports}
        rowKey={(row) => row.name}
        columns={[
          {
            key: 'port',
            label: t('Puerto'),
            render: (row) => (
              <span className='font-medium'>
                {row.name === '—' ? t('Sin puerto') : titleCase(row.name)}
              </span>
            ),
          },
          {
            key: 'transport',
            label: t('Tipo'),
            render: (row) => (
              <span className='text-default-500'>
                {row.transport ? t(TRANSPORT_LABELS[row.transport]) : '—'}
              </span>
            ),
          },
          {
            key: 'bar',
            label: t('Arribado · en tránsito'),
            render: (row) => (
              <div className='min-w-40'>
                <SplitBar split={row} max={max} by='shipments' />
              </div>
            ),
          },
          {
            key: 'shipments',
            label: t('Embarques'),
            align: 'right',
            render: (row) => (
              <span className='font-semibold'>
                {number(total(row, 'shipments'))}
              </span>
            ),
          },
          {
            key: 'arrived',
            label: t('Arribados'),
            align: 'right',
            render: (row) => number(row.arrived.shipments),
          },
          {
            key: 'inTransit',
            label: t('En tránsito'),
            align: 'right',
            render: (row) => number(row.inTransit.shipments),
          },
        ]}
        total={{
          port: t('Total'),
          shipments: number(data.totals.shipments),
          arrived: number(data.status.arrived.shipments),
          inTransit: number(data.status.inTransit.shipments),
        }}
      />
    </Panel>
  );
};

type MixRow = {
  key: string;
  label: string;
  split: ShipmentReportSplit;
  twoJUp?: number;
};

// Mix en cajas equivalentes, por variedad, calibre o etiqueta. En cereza, el
// de variedad trae la columna "2J up".
const MixPanel = ({
  data,
  items,
  title,
  column,
  format,
}: {
  data: ShipmentsToDateData;
  items: ShipmentsToDateData['varieties'];
  title: string;
  column: string;
  format: (label: string) => string;
}) => {
  const { t, number, percent } = useReportFormat();
  const max = Math.max(...items.map((item) => total(item, 'ceq')), 0);
  const rows: MixRow[] = items.map((item) => ({
    key: item.isOthers ? '__others__' : item.label,
    label: item.isOthers ? t('Otras') : format(item.label),
    split: item,
    twoJUp: item.twoJUp,
  }));
  const showTwoJUp = items.some((item) => item.twoJUp != null);
  const twoJUpTotal = items.reduce((sum, item) => sum + (item.twoJUp ?? 0), 0);
  const twoJUpShare = (twoJUp: number, ceq: number) =>
    percent(ceq ? twoJUp / ceq : 0);
  const arrivedShare = (split: ShipmentReportSplit) => {
    const ceq = total(split, 'ceq');
    return percent(ceq ? split.arrived.ceq / ceq : 0);
  };

  return (
    <Panel
      title={t('{{title}} · cajas eq. de {{kg}} kg', {
        title,
        kg: number(data.kgPerCeq),
      })}
      aside={<StatusLegend />}
    >
      <ReportTable<MixRow>
        label={title}
        rows={rows}
        rowKey={(row) => row.key}
        columns={[
          {
            key: 'item',
            label: column,
            render: (row) => <span className='font-medium'>{row.label}</span>,
          },
          {
            key: 'bar',
            label: t('Arribado · en tránsito'),
            render: (row) => (
              <div className='min-w-40'>
                <SplitBar split={row.split} max={max} by='ceq' />
              </div>
            ),
          },
          {
            key: 'arrivedShare',
            label: t('% arribado'),
            align: 'right',
            render: (row) => (
              <span className='text-default-500'>
                {arrivedShare(row.split)}
              </span>
            ),
          },
          {
            key: 'ceq',
            label: t('Cajas eq.'),
            align: 'right',
            render: (row) => number(total(row.split, 'ceq')),
          },
          {
            key: 'share',
            label: '%',
            align: 'right',
            render: (row) => (
              <span className='text-default-500'>
                {percent(
                  data.totals.ceq
                    ? total(row.split, 'ceq') / data.totals.ceq
                    : 0
                )}
              </span>
            ),
          },
          ...(showTwoJUp
            ? [
                {
                  key: 'twoJUp',
                  label: t('% 2J up'),
                  align: 'right' as const,
                  nowrap: true,
                  render: (row: MixRow) =>
                    twoJUpShare(row.twoJUp ?? 0, total(row.split, 'ceq')),
                },
              ]
            : []),
        ]}
        total={{
          item: t('Total'),
          bar: (
            <div className='min-w-40'>
              <SplitBar split={data.status} max={data.totals.ceq} by='ceq' />
            </div>
          ),
          arrivedShare: arrivedShare(data.status),
          ceq: number(data.totals.ceq),
          share: percent(1),
          ...(showTwoJUp
            ? { twoJUp: twoJUpShare(twoJUpTotal, data.totals.ceq) }
            : {}),
        }}
      />
    </Panel>
  );
};

type MatrixRow = { key: string; label: string; ceq: number[]; total: number };

// Etiqueta × formato: las etiquetas hacia abajo y los formatos de caja hacia
// el lado, en cajas equivalentes, con el total y el % de cada etiqueta.
const LabelFormatPanel = ({
  data,
  matrix,
}: {
  data: ShipmentsToDateData;
  matrix: NonNullable<ShipmentsToDateData['labelFormats']>;
}) => {
  const { t, number, percent } = useReportFormat();
  const title = t('Etiqueta × formato');
  const formats = [
    ...matrix.formats.map((format) => (format === '—' ? t('Sin formato') : format)),
    ...(matrix.hasOtherFormats ? [t('Otros')] : []),
  ];
  const rows: MatrixRow[] = matrix.rows.map((row) => ({
    key: row.isOthers ? '__others__' : row.label,
    label: row.isOthers
      ? t('Otras')
      : row.label === '—'
        ? t('Sin etiqueta')
        : row.label,
    ceq: row.ceq,
    total: row.total,
  }));
  const share = (value: number) =>
    percent(matrix.totals.total ? value / matrix.totals.total : 0);
  const cell = (value: number) =>
    value > 0 ? number(value) : <span className='text-default-300'>—</span>;

  return (
    <Panel
      title={t('{{title}} · cajas eq. de {{kg}} kg', {
        title,
        kg: number(data.kgPerCeq),
      })}
    >
      <ReportTable<MatrixRow>
        label={title}
        rows={rows}
        rowKey={(row) => row.key}
        columns={[
          {
            key: 'label',
            label: t('Etiqueta'),
            nowrap: true,
            render: (row) => <span className='font-medium'>{row.label}</span>,
          },
          ...formats.map((format, index) => ({
            key: `format-${index}`,
            label: format,
            align: 'right' as const,
            nowrap: true,
            render: (row: MatrixRow) => cell(row.ceq[index]),
          })),
          {
            key: 'total',
            label: t('Total'),
            align: 'right',
            render: (row) => <span className='font-medium'>{number(row.total)}</span>,
          },
          {
            key: 'share',
            label: '%',
            align: 'right',
            render: (row) => (
              <span className='text-default-500'>{share(row.total)}</span>
            ),
          },
        ]}
        total={{
          label: t('Total'),
          ...Object.fromEntries(
            matrix.totals.ceq.map((value, index) => [`format-${index}`, number(value)])
          ),
          total: number(matrix.totals.total),
          share: percent(1),
        }}
      />
    </Panel>
  );
};

// Despachos acumulados a la fecha: lo zarpado o arribado en la temporada,
// con el cumplimiento del plan comercial.
const ShipmentsToDateView = ({
  number: sectionNumber,
  data,
}: {
  number: number;
  data: ShipmentsToDateData;
}) => {
  const { t, number, percent } = useReportFormat();
  const layout = useReportLayout();
  const { totals, status, lastWeek, plan } = data;
  const title = t('Despachos acumulados a la fecha');

  if (totals.shipments === 0) {
    return (
      <section className='flex flex-col gap-6'>
        <SectionHeader number={sectionNumber} title={title} />
        <EmptySection>
          {t('Todavía no hay embarques zarpados esta temporada.')}
        </EmptySection>
      </section>
    );
  }

  const shipmentsDetail = [
    totals.sea > 0
      ? t('{{total}} marítimos', { total: number(totals.sea) })
      : null,
    totals.air > 0
      ? t('{{total}} aéreos', { total: number(totals.air) })
      : null,
    totals.vessels > 0
      ? t('{{total}} naves', { total: number(totals.vessels) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className='flex flex-col gap-8'>
      <SectionHeader number={sectionNumber} title={title} />

      <MetricStrip className={layout.kpiGrid}>
        <Metric
          label={t('Embarques')}
          value={number(totals.shipments)}
          detail={shipmentsDetail || undefined}
        >
          <SplitLine
            split={status}
            by='shipments'
            arrived={number(status.arrived.shipments)}
            inTransit={number(status.inTransit.shipments)}
          />
        </Metric>
        <Metric
          label={t('Cajas equivalentes')}
          value={number(totals.ceq)}
          detail={t('Caja de {{kg}} kg', { kg: number(data.kgPerCeq) })}
        >
          <SplitLine
            split={status}
            by='ceq'
            arrived={percent(totals.ceq ? status.arrived.ceq / totals.ceq : 0)}
            inTransit={percent(
              totals.ceq ? status.inTransit.ceq / totals.ceq : 0
            )}
          />
        </Metric>
        {plan.hasPlan ? (
          // Cumplimiento sobre el total del programa; abajo, a la fecha.
          <Metric
            label={t('Cumplimiento plan')}
            value={percent(
              plan.totals.committed
                ? plan.totals.shipped / plan.totals.committed
                : 0
            )}
            suffix={t('del total')}
            detail={t('{{shipped}} de {{committed}} {{unit}}', {
              shipped: formatPlanAmount(plan.totals.shipped, plan.unit, number),
              committed: formatPlanAmount(
                plan.totals.committed,
                plan.unit,
                number
              ),
              unit: t(PLAN_UNIT_LABELS[plan.unit]),
            })}
          >
            <ProgressLine
              value={percent(
                plan.totals.committedToDate
                  ? plan.totals.shippedToDate / plan.totals.committedToDate
                  : 0
              )}
              label={t('a la fecha · {{shipped}} de {{committed}}', {
                shipped: formatPlanAmount(
                  plan.totals.shippedToDate,
                  plan.unit,
                  number
                ),
                committed: formatPlanAmount(
                  plan.totals.committedToDate,
                  plan.unit,
                  number
                ),
              })}
              share={
                plan.totals.committedToDate
                  ? plan.totals.shippedToDate / plan.totals.committedToDate
                  : 0
              }
            />
            {data.restOfClients ? (
              <p className='text-xs text-default-500'>
                {t(
                  'Resto de los clientes: {{toDate}} a la fecha · {{total}} del total',
                  {
                    toDate: percent(
                      data.restOfClients.committedToDate
                        ? data.restOfClients.shippedToDate /
                            data.restOfClients.committedToDate
                        : 0
                    ),
                    total: percent(
                      data.restOfClients.committed
                        ? data.restOfClients.shipped /
                            data.restOfClients.committed
                        : 0
                    ),
                  }
                )}
              </p>
            ) : null}
          </Metric>
        ) : (
          <Metric
            label={t('Cumplimiento plan')}
            value={t('No aplica')}
            tone='muted'
            detail={t('Sin plan comercial cargado')}
          />
        )}
        <Metric
          label={t('Última semana')}
          value={lastWeek ? `+${number(lastWeek.ceq)}` : '—'}
          suffix={lastWeek ? t('cajas eq.') : undefined}
          detail={
            lastWeek
              ? t('sem {{week}} · {{shipments}} embarques', {
                  week: String(lastWeek.week).padStart(2, '0'),
                  shipments: number(lastWeek.shipments),
                })
              : undefined
          }
        >
          {lastWeek ? (
            <ProgressLine
              value={percent(totals.ceq ? lastWeek.ceq / totals.ceq : 0)}
              label={t('del acumulado')}
              share={totals.ceq ? lastWeek.ceq / totals.ceq : 0}
            />
          ) : null}
        </Metric>
      </MetricStrip>

      {plan.hasPlan ? <CommercialPlanChart data={plan} /> : null}
      <PortsPanel data={data} />
      <MixPanel
        data={data}
        items={data.varieties}
        title={t('Mix por variedad')}
        column={t('Variedad')}
        format={titleCase}
      />
      {/* Las fotos anteriores no traen calibres; sin calibres cargados tampoco aporta. */}
      {data.calibers?.some((item) => item.label !== '—') ? (
        <MixPanel
          data={data}
          items={data.calibers}
          title={t('Mix por calibre')}
          column={t('Calibre')}
          format={(label) => (label === '—' ? t('Sin calibre') : label)}
        />
      ) : null}
      {/* Sin etiquetas cargadas no aporta: sería una sola fila "Sin etiqueta". */}
      {data.labels?.some((item) => item.label !== '—') ? (
        <MixPanel
          data={data}
          items={data.labels}
          title={t('Mix por etiqueta')}
          column={t('Etiqueta')}
          format={(label) => (label === '—' ? t('Sin etiqueta') : label)}
        />
      ) : null}
      {/* Solo si hay etiquetas o formatos cargados; si no, sería una sola celda. */}
      {data.labelFormats &&
      (data.labelFormats.rows.some((row) => row.label !== '—') ||
        data.labelFormats.formats.some((format) => format !== '—')) ? (
        <LabelFormatPanel data={data} matrix={data.labelFormats} />
      ) : null}
    </section>
  );
};

export default ShipmentsToDateView;
